import { Injectable, NgZone, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, Unsubscribe } from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { NotificacaoPedidoDTO, TipoNotificacaoPedido } from '../../models/pedidos/notificacao-pedido.model';
import { DispositivoFcmService } from '../dispositivo/dispositivo-fcm.service';

/**
 * Service FCM Messaging que substitui NotificacaoPedidoWebsocketService.
 *
 * Responsabilidades:
 * - Solicitar permissao de notificacao push
 * - Obter token FCM e registrar no backend
 * - Escutar mensagens em foreground (onMessage)
 * - Emitir notificacoes$ (mesma interface do antigo WS service)
 * - Remover token no logout
 */
@Injectable({ providedIn: 'root' })
export class NotificacaoFcmService implements OnDestroy {

  /** Observable de notificacoes recebidas (mesma interface do antigo WS service) */
  readonly notificacoes$: Observable<NotificacaoPedidoDTO>;

  private notificacoesSubject = new Subject<NotificacaoPedidoDTO>();
  private messaging: Messaging | null = null;
  private unsubscribeOnMessage: Unsubscribe | null = null;
  private firebaseApp: FirebaseApp | null = null;

  private readonly FCM_TOKEN_KEY = 'fcmToken';

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private ngZone: NgZone,
    private dispositivoService: DispositivoFcmService,
  ) {
    this.notificacoes$ = this.notificacoesSubject.asObservable();
  }

  ngOnDestroy(): void {
    this.limparListeners();
    this.notificacoesSubject.complete();
  }

  /**
   * Inicializa FCM: solicita permissao, obtem token, registra no backend e escuta mensagens.
   * Deve ser chamado apos login (quando isAuthenticated$ emite true).
   */
  inicializar(): void {
    // Guard: nao executar no servidor (SSR/prerender)
    if (!isPlatformBrowser(this.platformId)) return;

    // Guard: so inicializar se usuario autenticado
    const token = sessionStorage.getItem('authToken');
    if (!token) {
      console.warn('[FCM] Abortando inicializacao: authToken ausente no sessionStorage');
      return;
    }

    this.inicializarFirebase();

    if (!this.messaging) {
      console.warn('[FCM] Firebase Messaging nao disponivel (browser nao suporta ou configuracao invalida)');
      return;
    }

    this.solicitarPermissaoERegistrar();
    this.escutarMensagens();
  }

  /**
   * Desativa FCM: remove token do backend e limpa estado local.
   * Deve ser chamado no logout.
   */
  desativar(): void {
    // Guard: nao executar no servidor (SSR/prerender)
    if (!isPlatformBrowser(this.platformId)) return;

    const fcmToken = sessionStorage.getItem(this.FCM_TOKEN_KEY);

    if (fcmToken) {
      // Desativar token no backend (fire-and-forget)
      this.dispositivoService.remover({ token: fcmToken }).subscribe({
        next: () => {},
        error: (err) => console.warn('[FCM] Erro ao desativar token no backend:', err),
      });
    }

    sessionStorage.removeItem(this.FCM_TOKEN_KEY);
    this.limparListeners();
  }

  /** Inicializa Firebase App e Messaging (reutiliza app existente se ja inicializado) */
  private inicializarFirebase(): void {
    try {
      const apps = getApps();
      this.firebaseApp = apps.length > 0 ? apps[0] : initializeApp(environment.firebase);
      this.messaging = getMessaging(this.firebaseApp);
    } catch (err) {
      console.error('[FCM] Erro ao inicializar Firebase Messaging:', err);
      this.messaging = null;
    }
  }

  /** Solicita permissao push, obtem token FCM e registra no backend */
  private async solicitarPermissaoERegistrar(): Promise<void> {
    try {
      // Guard: verificar se vapidKey foi configurada (nao e placeholder)
      if (!environment.firebase.vapidKey || environment.firebase.vapidKey.startsWith('PREENCHER')) {
        console.error('[FCM] CONFIGURACAO INVALIDA: vapidKey nao foi preenchida no environment. Acesse o Firebase Console → Project Settings → Cloud Messaging → Web Push certificates e copie a "Key pair" para environment.firebase.vapidKey');
        return;
      }

      // Guard: verificar se messagingSenderId foi configurado
      if (!environment.firebase.messagingSenderId || environment.firebase.messagingSenderId.startsWith('PREENCHER')) {
        console.error('[FCM] CONFIGURACAO INVALIDA: messagingSenderId nao foi preenchido no environment. Acesse o Firebase Console → Project Settings e copie o "Sender ID" para environment.firebase.messagingSenderId');
        return;
      }

      // Solicitar permissao de notificacao
      const permissao = await Notification.requestPermission();
      if (permissao !== 'granted') {
        console.warn('[FCM] Permissao de notificacao negada pelo usuario');
        return;
      }

      // Registrar Service Worker do Firebase para receber mensagens push
      let swRegistration: ServiceWorkerRegistration | undefined;
      if ('serviceWorker' in navigator) {
        swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      }

      // Obter token FCM (passando SW registration para garantir vinculo correto)
      const fcmToken = await getToken(this.messaging!, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: swRegistration,
      });

      if (!fcmToken) {
        console.warn('[FCM] Nao foi possivel obter token FCM');
        return;
      }

      // Persistir token no sessionStorage
      sessionStorage.setItem(this.FCM_TOKEN_KEY, fcmToken);

      // Registrar token no backend (tokens orfaos sao limpos pelo backend via erro UNREGISTERED)
      this.dispositivoService.registrar({
        token: fcmToken,
        plataforma: 'WEB',
        deviceId: this.getOrCreateDeviceId(),
        modelo: this.obterModeloBrowser(),
        osVersao: this.obterOsVersao(),
        appVersao: this.obterAppVersao(),
      }).subscribe({
        next: () => {},
        error: (err) => console.warn('[FCM] Erro ao registrar token no backend (sistema continua funcionando):', err),
      });
    } catch (err) {
      console.error('[FCM] Erro ao solicitar permissao ou obter token:', err);
      // Degradacao graceful: sistema funciona normalmente sem push
    }
  }

  /**
   * Gera ou recupera um UUID unico para este browser/perfil.
   * Persistido em localStorage para sobreviver a sessoes e recarregamentos.
   */
  private getOrCreateDeviceId(): string {
    const KEY = 'fcm_device_id';
    let deviceId = localStorage.getItem(KEY);
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem(KEY, deviceId);
    }
    return deviceId;
  }

  /**
   * Extrai nome e versao do browser.
   * Usa navigator.userAgentData (API moderna) com fallback para userAgent parsing.
   * Retorna ex: "Chrome 120", "Firefox 115", "Safari 17".
   */
  private obterModeloBrowser(): string {
    try {
      // API moderna (Chromium-based browsers)
      const uaData = (navigator as any).userAgentData;
      if (uaData?.brands?.length) {
        // Filtrar "Not" brands (ex: "Not_A Brand") e pegar a mais relevante
        const brand = uaData.brands.find(
          (b: any) => !b.brand.startsWith('Not') && b.brand !== 'Chromium'
        ) || uaData.brands[0];
        return `${brand.brand} ${brand.version}`;
      }
    } catch (_) {}

    // Fallback: parsing do userAgent
    const ua = navigator.userAgent;
    const match =
      ua.match(/(?:Chrome|Chromium)\/([\d.]+)/) ||
      ua.match(/Firefox\/([\d.]+)/) ||
      ua.match(/Safari\/([\d.]+)/) ||
      ua.match(/Edg\/([\d.]+)/) ||
      ua.match(/OPR\/([\d.]+)/);

    if (match) {
      let nome = 'Chrome';
      if (ua.includes('Firefox')) nome = 'Firefox';
      else if (ua.includes('Edg')) nome = 'Edge';
      else if (ua.includes('OPR')) nome = 'Opera';
      else if (ua.includes('Safari') && !ua.includes('Chrome')) nome = 'Safari';
      return `${nome} ${match[1].split('.')[0]}`;
    }

    return 'Browser desconhecido';
  }

  /**
   * Obtem a plataforma/OS do browser.
   * Usa navigator.userAgentData.platform (API moderna) com fallback para navigator.platform.
   */
  private obterOsVersao(): string {
    try {
      const uaData = (navigator as any).userAgentData;
      if (uaData?.platform) {
        return uaData.platform;
      }
    } catch (_) {}

    // Fallback: navigator.platform (depreciado mas ainda amplamente suportado)
    return navigator.platform || 'Desconhecido';
  }

  /**
   * Retorna a versao da aplicacao web.
   * Versao fixa - deve ser atualizada junto com package.json.
   * TODO: Considerar injetar via environment ou version.ts em build futuro
   */
  private obterAppVersao(): string {
    return '1.0.0';
  }

  /** Escuta mensagens FCM em foreground e emite no Subject */
  private escutarMensagens(): void {
    if (!this.messaging) return;

    // Limpar listener anterior se existir
    if (this.unsubscribeOnMessage) {
      this.unsubscribeOnMessage();
      this.unsubscribeOnMessage = null;
    }

    this.unsubscribeOnMessage = onMessage(this.messaging, (payload) => {
      // Executar dentro da NgZone para que o Angular detecte as mudancas
      this.ngZone.run(() => {
        const data = payload.data;
        if (!data || !data['tipo']) {
          console.warn('[FCM] Payload sem dados ou sem tipo, ignorando');
          return;
        }

        const notificacao = this.converterPayload(data as Record<string, string>);
        this.notificacoesSubject.next(notificacao);
      });
    });
  }

  /**
   * Converte payload FCM data (Map<string,string>) para NotificacaoPedidoDTO.
   * Todos os valores FCM chegam como string, entao pedidoId precisa de conversao.
   */
  private converterPayload(data: Record<string, string>): NotificacaoPedidoDTO {
    return {
      tipo: data['tipo'] as TipoNotificacaoPedido,
      pedidoId: Number(data['pedidoId']),
      codigoDePedido: data['codigoDePedido'] || '',
      nomeCliente: data['nomeCliente'] || '',
      estadoAnterior: data['estadoAnterior'] || '',   // "" em NOVO_PEDIDO
      estadoAtual: data['estadoAtual'] || '',
      timestamp: data['timestamp'] || '',
      tipoEntrega: data['tipoEntrega'] || undefined,
    };
  }

  /** Limpa listeners de onMessage */
  private limparListeners(): void {
    if (this.unsubscribeOnMessage) {
      this.unsubscribeOnMessage();
      this.unsubscribeOnMessage = null;
    }
  }
}
